/**
 * Concept-graph algorithms.
 *
 * "That mathematical knowledge graph is the real product" (§32). Everything the
 * platform does that feels intelligent — what to study next, why a student is
 * stuck, what a wrong answer implies — is a query against this graph, so it
 * lives in one place and is tested independently of React.
 */

import type { Concept } from './types.ts';

export type ConceptGraph = {
  concepts: Map<string, Concept>;
  /** concept id → ids it depends on. */
  prerequisites: Map<string, string[]>;
  /** concept id → ids that depend on it. Reverse edges, precomputed. */
  dependents: Map<string, string[]>;
};

export function buildGraph(concepts: readonly Concept[]): ConceptGraph {
  const byId = new Map<string, Concept>();
  for (const concept of concepts) {
    if (byId.has(concept.id)) throw new Error(`Duplicate concept id: ${concept.id}`);
    byId.set(concept.id, concept);
  }

  const prerequisites = new Map<string, string[]>();
  const dependents = new Map<string, string[]>();
  for (const concept of concepts) {
    prerequisites.set(
      concept.id,
      concept.prerequisites.filter((id) => byId.has(id)),
    );
    if (!dependents.has(concept.id)) dependents.set(concept.id, []);
  }
  for (const concept of concepts) {
    for (const prereq of prerequisites.get(concept.id) ?? []) {
      dependents.get(prereq)?.push(concept.id);
    }
  }

  return { concepts: byId, prerequisites, dependents };
}

/** Direct prerequisites, in author order. */
export function directPrerequisites(graph: ConceptGraph, id: string): Concept[] {
  return (graph.prerequisites.get(id) ?? []).map((p) => graph.concepts.get(p)).filter(isConcept);
}

export function directDependents(graph: ConceptGraph, id: string): Concept[] {
  return (graph.dependents.get(id) ?? []).map((p) => graph.concepts.get(p)).filter(isConcept);
}

const isConcept = (value: Concept | undefined): value is Concept => value !== undefined;

/**
 * Every concept reachable backwards from `id` — the full foundation a student
 * needs. Breadth-first so nearer prerequisites come first, which is the order a
 * struggling student should revisit them in.
 */
export function allPrerequisites(graph: ConceptGraph, id: string): string[] {
  const seen = new Set<string>();
  const queue = [...(graph.prerequisites.get(id) ?? [])];
  const out: string[] = [];
  while (queue.length > 0) {
    const current = queue.shift() as string;
    if (seen.has(current) || current === id) continue;
    seen.add(current);
    out.push(current);
    queue.push(...(graph.prerequisites.get(current) ?? []));
  }
  return out;
}

/** Every concept that becomes reachable once `id` is understood. */
export function allDependents(graph: ConceptGraph, id: string): string[] {
  const seen = new Set<string>();
  const queue = [...(graph.dependents.get(id) ?? [])];
  const out: string[] = [];
  while (queue.length > 0) {
    const current = queue.shift() as string;
    if (seen.has(current) || current === id) continue;
    seen.add(current);
    out.push(current);
    queue.push(...(graph.dependents.get(current) ?? []));
  }
  return out;
}

export class CycleError extends Error {
  constructor(readonly cycle: string[]) {
    super(`Prerequisite cycle: ${cycle.join(' → ')}`);
    this.name = 'CycleError';
  }
}

/**
 * A valid teaching order. Kahn's algorithm with a deterministic tie-break, so
 * the same curriculum always produces the same order — content diffs stay
 * readable and snapshots stay stable.
 */
export function topologicalOrder(graph: ConceptGraph): string[] {
  const indegree = new Map<string, number>();
  for (const id of graph.concepts.keys()) {
    indegree.set(id, (graph.prerequisites.get(id) ?? []).length);
  }

  const ready = [...indegree.entries()]
    .filter(([, degree]) => degree === 0)
    .map(([id]) => id)
    .sort();

  const order: string[] = [];
  while (ready.length > 0) {
    const id = ready.shift() as string;
    order.push(id);
    for (const dependent of graph.dependents.get(id) ?? []) {
      const remaining = (indegree.get(dependent) ?? 0) - 1;
      indegree.set(dependent, remaining);
      if (remaining === 0) {
        ready.push(dependent);
        ready.sort();
      }
    }
  }

  if (order.length !== graph.concepts.size) {
    const stuck = [...graph.concepts.keys()].filter((id) => !order.includes(id));
    throw new CycleError(findCycle(graph, stuck));
  }
  return order;
}

function findCycle(graph: ConceptGraph, candidates: string[]): string[] {
  const start = candidates[0];
  if (!start) return [];
  const path: string[] = [];
  const onPath = new Set<string>();

  const walk = (id: string): string[] | null => {
    if (onPath.has(id)) return [...path.slice(path.indexOf(id)), id];
    if (!candidates.includes(id)) return null;
    path.push(id);
    onPath.add(id);
    for (const next of graph.prerequisites.get(id) ?? []) {
      const found = walk(next);
      if (found) return found;
    }
    path.pop();
    onPath.delete(id);
    return null;
  };

  return walk(start) ?? [start];
}

/**
 * Layered layout for the Math Map: layer = longest path from a root, so a
 * concept always sits below everything it depends on. Within a layer, nodes are
 * grouped by strand and then by author order, which keeps related mathematics
 * side by side instead of scattered.
 */
export type GraphLayout = {
  nodes: { id: string; layer: number; order: number }[];
  layers: string[][];
};

/**
 * `include` narrows the drawing without changing the mathematics. Depth is
 * always computed over the whole graph — a concept's row means "how far into
 * the subject this is", and re-deriving it from a filtered subgraph would put a
 * Grade 12 concept on the top row the moment its prerequisites sat in another
 * strand.
 *
 * What filtering changes is *occupancy*: only included concepts take a slot,
 * and rows left empty are dropped. Hiding nodes without compacting is what made
 * a single-strand view unreadable — twenty concepts keeping their slots in a
 * two-hundred-wide row, so the map zoomed out to fit blank space.
 */
export function layoutConceptGraph(graph: ConceptGraph, include?: ReadonlySet<string>): GraphLayout {
  const order = topologicalOrder(graph);
  const depth = new Map<string, number>();

  for (const id of order) {
    const prereqs = graph.prerequisites.get(id) ?? [];
    const deepest = prereqs.reduce((max, p) => Math.max(max, (depth.get(p) ?? 0) + 1), 0);
    depth.set(id, deepest);
  }

  const byDepth: string[][] = [];
  for (const id of order) {
    if (include && !include.has(id)) continue;
    const layer = depth.get(id) ?? 0;
    (byDepth[layer] ??= []).push(id);
  }
  const layers: string[][] = byDepth.filter((ids) => ids.length > 0);

  const strandOrder = new Map<string, number>();
  for (const [index, id] of order.entries()) {
    const strand = graph.concepts.get(id)?.strand ?? '';
    if (!strandOrder.has(strand)) strandOrder.set(strand, index);
  }

  const nodes: GraphLayout['nodes'] = [];
  layers.forEach((ids, layer) => {
    ids.sort((a, b) => {
      const strandA = strandOrder.get(graph.concepts.get(a)?.strand ?? '') ?? 0;
      const strandB = strandOrder.get(graph.concepts.get(b)?.strand ?? '') ?? 0;
      if (strandA !== strandB) return strandA - strandB;
      return order.indexOf(a) - order.indexOf(b);
    });
    ids.forEach((id, index) => nodes.push({ id, layer, order: index }));
  });

  return { nodes, layers };
}

/* -------------------------------------------------------------------------- */
/* Queries the learner experience is built on                                 */
/* -------------------------------------------------------------------------- */

/** A concept is "reachable" when every prerequisite is already mastered. */
export function isUnlocked(graph: ConceptGraph, id: string, mastered: ReadonlySet<string>): boolean {
  return (graph.prerequisites.get(id) ?? []).every((p) => mastered.has(p));
}

export function missingPrerequisites(
  graph: ConceptGraph,
  id: string,
  mastered: ReadonlySet<string>,
): string[] {
  return (graph.prerequisites.get(id) ?? []).filter((p) => !mastered.has(p));
}

/**
 * The frontier: unmastered concepts whose prerequisites are all met. This is
 * exactly the set a student can start today, and the input to recommendations.
 */
export function frontier(graph: ConceptGraph, mastered: ReadonlySet<string>): string[] {
  const out: string[] = [];
  for (const id of topologicalOrder(graph)) {
    if (mastered.has(id)) continue;
    if (isUnlocked(graph, id, mastered)) out.push(id);
  }
  return out;
}

/**
 * The shortest legal study plan from what a student knows to a target concept —
 * the "how do I get to Calculus from here" query. Returns concepts in an order
 * that never violates a prerequisite, target last.
 */
export function pathTo(graph: ConceptGraph, target: string, mastered: ReadonlySet<string>): string[] {
  if (!graph.concepts.has(target)) return [];
  if (mastered.has(target)) return [];

  const needed = new Set<string>([target]);
  const queue = [target];
  while (queue.length > 0) {
    const current = queue.shift() as string;
    for (const prereq of graph.prerequisites.get(current) ?? []) {
      if (mastered.has(prereq) || needed.has(prereq)) continue;
      needed.add(prereq);
      queue.push(prereq);
    }
  }

  return topologicalOrder(graph).filter((id) => needed.has(id));
}

/** Distance in "concepts still to learn", used to sort goals by reachability. */
export function distanceTo(graph: ConceptGraph, target: string, mastered: ReadonlySet<string>): number {
  return pathTo(graph, target, mastered).length;
}
